import { AnalyticsRoute } from "@ui/navigation/analytics-route";
import { AuthenticatedRoutes, DebugRoutes, UnAuthenticatedRoutes } from "@ui/navigation/authenticated-routes";
import { RoutesPath } from "@ui/navigation/routes";
import { AccountDeletedPage } from "@ui/pages/deletion-flow/account-deleted-page";
import { LeavingReasonsPage } from "@ui/pages/deletion-flow/leaving-reasons-page";
import { ThankYouSurvey } from "@ui/pages/home/components/survey/thank-you-survey";
import { HomePage } from "@ui/pages/home/home-page";
import { PrivacyNoticePage } from "@ui/pages/legal-content/privacy-notice-page";
import { TermsOfUsePage } from "@ui/pages/legal-content/terms-of-use-page";
import VipLoginPage from "@ui/pages/login/vip-login-page";
import VipLoginRedirectionPage from "@ui/pages/login/vip-login-redirection-page";
import { NotificationCenterPage } from "@ui/pages/notification-center/notification-center-page";
import { PatientDetailsPage } from "@ui/pages/patient-details/patient-details-page";
import { ProgramBuilderPage } from "@ui/pages/program-builder/program-builder-page";
import { InviteYourPtPage } from "@ui/pages/prospect/campaigns/invite-your-pt/invite-your-pt-page";
import { RegisterPrivacyNoticePage } from "@ui/pages/register/register-privacy-notice-page";
import { RegisterTermsOfUsePage } from "@ui/pages/register/register-terms-of-use-page";
import RejectedPtPage from "@ui/pages/rejected-pt/rejected-pt-page";
import { SettingsPage } from "@ui/pages/settings/settings-page";
import TemplateBuilderPage from "@ui/pages/template-builder/template-builder";
import { TemplateListingPage } from "@ui/pages/program/program-listing-page";
import { ExerciseListingPage } from "@ui/pages/exercise/exercise-listing-page";
import { LearnPage } from "@ui/pages/learn/learn-page";
import LearnDocumentPage from "@ui/pages/learn/learn-document-page";
import { RegisterProfessionalInformationPage } from "@ui/pages/validate-professional-information/register-professional-information-page";
import { NetworkError } from "@ui/components/common/error";
import * as React from "react";
import { createBrowserRouter, RouterProvider, useRouteError } from "react-router-dom";
import { SurveyRoute } from "@ui/navigation/survey-route";
import { GetStartedPage } from "@ui/pages/get-started/get-started";

const CHUNK_RELOAD_SESSION_KEY = "chunk-reload-at";
const CHUNK_RELOAD_GUARD_MS = 10_000;
const fullScreenCenterStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" };

const isChunkLoadError = (error: unknown): boolean =>
	error instanceof Error &&
	(error.message.includes("Failed to fetch dynamically imported module") || error.name === "ChunkLoadError");

const reloadOnChunkError = (): void => {
	try {
		const lastReload = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
		const now = Date.now();
		if (!lastReload || now - parseInt(lastReload, 10) > CHUNK_RELOAD_GUARD_MS) {
			sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, String(now));
			window.location.reload();
		}
	} catch {
		window.location.reload();
	}
};

const hasAlreadyReloaded = (): boolean => {
	try {
		const lastReload = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
		return !!lastReload && Date.now() - parseInt(lastReload, 10) <= CHUNK_RELOAD_GUARD_MS;
	} catch {
		return false;
	}
};

const RouteChunkErrorHandler = (): JSX.Element | null => {
	const error = useRouteError();
	const alreadyReloaded = hasAlreadyReloaded();

	React.useEffect(() => {
		if (isChunkLoadError(error) && !alreadyReloaded) {
			reloadOnChunkError();
		}
	}, [error, alreadyReloaded]);

	if (isChunkLoadError(error) && alreadyReloaded) {
		return (
			<div style={fullScreenCenterStyle}>
				<NetworkError onRefresh={() => window.location.reload()} />
			</div>
		);
	}

	if (!isChunkLoadError(error)) {
		return (
			<div style={fullScreenCenterStyle}>
				<NetworkError />
			</div>
		);
	}

	return null;
};

interface ChunkErrorBoundaryState {
	hasError: boolean;
	isChunkError: boolean;
}

class ChunkLoadErrorBoundary extends React.Component<React.PropsWithChildren, ChunkErrorBoundaryState> {
	constructor(props: React.PropsWithChildren) {
		super(props);
		this.state = { hasError: false, isChunkError: false };
	}

	static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
		return { hasError: true, isChunkError: isChunkLoadError(error) };
	}

	componentDidCatch(error: Error): void {
		if (!isChunkLoadError(error)) return;
		reloadOnChunkError();
	}

	render(): React.ReactNode {
		if (this.state.hasError) return null;
		return this.props.children;
	}
}

const RegisterPage = React.lazy(() => import("@ui/pages/register/register-page"));
const LoginPage = React.lazy(() => import("@ui/pages/login/login"));
const ForgotPasswordPage = React.lazy(() => import("@ui/pages/login/forgot-password"));
const DebugPage = React.lazy(() => import("@ui/pages/debug/debug-page"));
const ErrorPage = React.lazy(() => import("@ui/pages/error-page"));
const DemoRequestPage = React.lazy(() => import("@ui/pages/prospect/campaigns/demo-request/demo-request-page"));

const router = createBrowserRouter([
	{
		path: "/",
		element: <AuthenticatedRoutes />,
		errorElement: <RouteChunkErrorHandler />,
		children: [
			{
				path: RoutesPath.Home,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<HomePage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.Rejected,
				element: (
					<AnalyticsRoute>
						<RejectedPtPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.PatientDetails,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<PatientDetailsPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.ProgramBuilder,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<ProgramBuilderPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.ProgramList,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<TemplateListingPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.TemplateBuilder,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<TemplateBuilderPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.ExerciseList,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<ExerciseListingPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.Learn,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<LearnPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.LearnDocument,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<LearnDocumentPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.GetStarted,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<GetStartedPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.PrivacyNotice,
				element: (
					<AnalyticsRoute>
						<PrivacyNoticePage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.Settings,
				element: (
					<AnalyticsRoute>
						<SettingsPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.LeavingReasons,
				element: (
					<AnalyticsRoute>
						<LeavingReasonsPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.TermsOfUse,
				element: (
					<AnalyticsRoute>
						<TermsOfUsePage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.NotificationCenter,
				element: (
					<AnalyticsRoute>
						<SurveyRoute>
							<NotificationCenterPage />
						</SurveyRoute>
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.ProfessionalInformation,
				element: (
					<AnalyticsRoute>
						<RegisterProfessionalInformationPage />
					</AnalyticsRoute>
				),
			},
			{
				path: "/",
				element: <DebugRoutes />,
				children: [{ path: RoutesPath.Debug, index: true, element: <DebugPage /> }],
			},
			{ path: "*", element: <ErrorPage /> },
		],
	},
	{
		path: "/",
		element: <UnAuthenticatedRoutes />,
		errorElement: <RouteChunkErrorHandler />,
		children: [
			{
				path: RoutesPath.Register,
				element: (
					<AnalyticsRoute>
						<RegisterPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.Login,
				element: (
					<AnalyticsRoute>
						<LoginPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.VipLogin,
				element: (
					<AnalyticsRoute>
						<VipLoginPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.VipLoginRedirection,
				element: (
					<AnalyticsRoute>
						<VipLoginRedirectionPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.DemoRequest,
				element: (
					<AnalyticsRoute>
						<DemoRequestPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.InviteYourPt,
				element: (
					<AnalyticsRoute>
						<InviteYourPtPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.ForgotPassword,
				element: (
					<AnalyticsRoute>
						<ForgotPasswordPage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.AccountDeleted,
				element: (
					<AnalyticsRoute>
						<AccountDeletedPage />
					</AnalyticsRoute>
				),
			},
		],
	},
	{
		path: RoutesPath.ThankYouSurvey,
		element: (
			<AnalyticsRoute>
				<ThankYouSurvey />
			</AnalyticsRoute>
		),
		errorElement: <RouteChunkErrorHandler />,
	},
	{
		path: "/",
		errorElement: <RouteChunkErrorHandler />,
		children: [
			{
				path: RoutesPath.RegisterPrivacyNotice,
				element: (
					<AnalyticsRoute>
						<RegisterPrivacyNoticePage />
					</AnalyticsRoute>
				),
			},
			{
				path: RoutesPath.RegisterTermsOfUse,
				element: (
					<AnalyticsRoute>
						<RegisterTermsOfUsePage />
					</AnalyticsRoute>
				),
			},
		],
	},
]);

export const AppRouter = (): JSX.Element => (
	<ChunkLoadErrorBoundary>
		<React.Suspense fallback={null}>
			<RouterProvider router={router} />
		</React.Suspense>
	</ChunkLoadErrorBoundary>
);
